import CloseIcon from "@mui/icons-material/Close";
import { Backdrop, Box, Fade, IconButton, Modal } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";

interface ImagePreviewModalProps {
    open: boolean;
    imageUrl: string;
    alt?: string;
    onClose: () => void;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({ open, imageUrl, alt = "Preview", onClose }) => {
    const [loaded, setLoaded] = useState(false);

    // Reset loaded state when modal opens with new image
    useEffect(() => {
        if (open) {
            setLoaded(false);
        }
    }, [open, imageUrl]);

    // Handle keyboard events
    const handleKeyDown = useCallback(
        (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                onClose();
            }
        },
        [onClose]
    );

    useEffect(() => {
        if (open) {
            document.addEventListener("keydown", handleKeyDown);
            return () => {
                document.removeEventListener("keydown", handleKeyDown);
            };
        }
    }, [open, handleKeyDown]);

    return (
        <Modal
            open={open}
            onClose={onClose}
            closeAfterTransition
            slots={{ backdrop: Backdrop }}
            slotProps={{
                backdrop: {
                    timeout: 300,
                    sx: {
                        backgroundColor: "rgba(0, 0, 0, 0.85)",
                    },
                },
            }}
        >
            <Fade in={open}>
                <Box
                    sx={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        transform: "translate(-50%, -50%)",
                        outline: "none",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        maxWidth: "90vw",
                        maxHeight: "90vh",
                    }}
                >
                    {/* Close Button */}
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "fixed",
                            top: 16,
                            right: 16,
                            color: "#fff",
                            backgroundColor: "rgba(0, 0, 0, 0.5)",
                            "&:hover": {
                                backgroundColor: "rgba(0, 0, 0, 0.7)",
                            },
                            zIndex: 1,
                        }}
                    >
                        <CloseIcon />
                    </IconButton>

                    {/* Image */}
                    <Box
                        component='img'
                        src={imageUrl}
                        alt={alt}
                        onLoad={() => setLoaded(true)}
                        sx={{
                            maxWidth: "90vw",
                            maxHeight: "90vh",
                            objectFit: "contain",
                            borderRadius: 2,
                            boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
                            opacity: loaded ? 1 : 0,
                            transition: "opacity 0.3s ease",
                            cursor: "zoom-out",
                        }}
                        onClick={onClose}
                    />

                    {/* Loading indicator */}
                    {!loaded && (
                        <Box
                            sx={{
                                position: "absolute",
                                color: "#fff",
                                fontSize: "1rem",
                            }}
                        >
                            Loading...
                        </Box>
                    )}
                </Box>
            </Fade>
        </Modal>
    );
};

export default ImagePreviewModal;
